import { differenceInSeconds } from 'date-fns'
import useAppConfig from 'hooks/redux/useAppConfig'
import useContact from 'hooks/redux/useContact'
import useChatConnections from 'hooks/redux/useChatConnections'
import useContactLogForm from 'hooks/useContactLogForm'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { completeChatACW } from 'store/chat/chat.actions'
import { completeTaskACW } from 'store/tasks/tasks.actions'
import { completeCallACW } from 'store/contact/contact.actions'
import useSettings from './useSettings'
import useTaskConnections from './useTaskConnections'

const DEFAULT_ACW_TIME_SECONDS = 60

export default function useACWTimeLeft() {
    const completeContact = (id: string) => {
        if (chat) {
            dispatch(completeChatACW(id))
        } else if (task) {
            dispatch(completeTaskACW(id))
        } else {
            dispatch(completeCallACW(id))
        }
    }
    const getInitialACWTime = (channel?: string) => {
        if (contactLog?.form?.wrapTimeSeconds) {
            return contactLog.form.wrapTimeSeconds
        }

        switch (channel) {
            case 'CHAT':
                return (
                    settings?.appConfigurations?.chat?.afterCallWorkTimeoutSeconds ??
                    chatAfterCallWorkInitialTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            case 'EMAIL':
                return (
                    settings?.appConfigurations?.task?.afterCallWorkTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            case 'VOICE':
                return (
                    settings?.appConfigurations?.afterCallWorkTimeoutSeconds ??
                    afterCallWorkInitialTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            default:
                return DEFAULT_ACW_TIME_SECONDS
        }
    }

    const contactLog = useContactLogForm()
    const { settings } = useSettings()
    const { chatAfterCallWorkInitialTimeoutSeconds, afterCallWorkInitialTimeoutSeconds } = useAppConfig()
    const dispatch = useDispatch()
    const chat = useChatConnections('selected')
    const task = useTaskConnections('selected')
    const voice = useContact()
    const contact = chat ?? task ?? {...voice, id: voice?.ID}
    const channel = contact.channel ?? 'CHAT'
    const acwInitialTimeLeft = getInitialACWTime(channel)
    const [acwTimeLeft, setAcwTimeLeft] = useState<number | null>(null)
    const [selectedChatID, setSelectedChatID] = useState<null | string>(null)
    const [selectedTaskID, setSelectedTaskID] = useState<null | string>(null)

    const updateAcwTimeLeft = () => {
        if (contact?.acwStartedAt && !contact?.acwExtendedAt) {
            //  calculate the seconds remaining for the after call work
            const timeLeftInSeconds =
                acwInitialTimeLeft - differenceInSeconds(Date.now(), contact.acwStartedAt)
            // if theres acw time remaining update the time left or end the contact if the timer has reached 0
            timeLeftInSeconds >= 0
                ? setAcwTimeLeft(timeLeftInSeconds)
                : completeContact(contact.id!)
        }
    }

    useEffect(() => {
        //  if the selected chat has changed clear the acw time left and change the selectedChat
        if (chat?.id && chat?.id !== selectedChatID) {
            if (acwTimeLeft) setAcwTimeLeft(null)
            setSelectedChatID(chat.id)
        }
    }, [chat])

    useEffect(() => {
        //  if the selected task has changed clear the acw time left and change the selectedTask
        if (task?.id && task?.id !== selectedTaskID) {
            if (acwTimeLeft) setAcwTimeLeft(null)
            setSelectedTaskID(task.id)
        }
    }, [task])

    useEffect(() => {
        //   recalculate the timer if the new selected chat is in after call work
        if (chat?.acw === true && !chat.acwExtendedAt) {
            updateAcwTimeLeft()
        } else {
            acwTimeLeft && setAcwTimeLeft(null)
        }
    }, [selectedChatID])

    useEffect(() => {
        //   recalculate the timer if the new selected task is in after call work
        if (task?.acw === true && !task.acwExtendedAt) {
            updateAcwTimeLeft()
        } else {
            acwTimeLeft && setAcwTimeLeft(null)
        }
    }, [selectedTaskID])
    
    useEffect(() => {
        if (contact?.id) {
            updateAcwTimeLeft()
        }
    }, [contact?.acwStartedAt])

    return acwTimeLeft
}
